import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../types/core_types.js";

export const ShockwaveLayout = {
  aPosTime:    AttributeSize.Vec4, // x, y, z, start_time
  aParams:     AttributeSize.Vec3, // max_scale, duration, deceleration_power
  aFadeParams: AttributeSize.Vec2, // fade_start, max_alpha
} as const;

export class VfxShockwaveBatch extends InstanceBatch<typeof ShockwaveLayout>
{
  private static readonly MAX_INSTANCES : number = 20;
  private static readonly RENDER_ORDER  : number = 20;

  private static cached_geometry : THREE.BufferGeometry | null = null;
  private texture_initialized    : boolean = false;

  public constructor()
  {
    const base_geometry = VfxShockwaveBatch.getGeometry();

    let atlas_texture: THREE.Texture | null = null;
    const glow_uv_vec = new THREE.Vector4();

    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
      const glow_uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.ShockwaveGlow1 );
      glow_uv_vec.set( glow_uv.min_x, glow_uv.min_y, glow_uv.max_x, glow_uv.max_y );
    }
    catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:    { value: 0.0 },
        uTexture: { value: atlas_texture },
        uGlowUv:  { value: glow_uv_vec },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosTime;
        attribute vec3 aParams;     // x: max_scale, y: duration, z: deceleration_power
        attribute vec2 aFadeParams; // x: fade_start, y: max_alpha

        varying vec2 vQuadUv;
        varying float vAlpha;

        void main() {
          float start_time = aPosTime.w;
          float duration = aParams.y;
          float age = max( 0.0, uTime - start_time );
          float progress = duration > 0.0 ? clamp( age / duration, 0.0, 1.0 ) : 0.0;

          vQuadUv = uv;

          // Fade out starts at fade_start (0.0..1.0)
          float fade_start = aFadeParams.x;
          float max_alpha = aFadeParams.y;
          float fade = 1.0 - clamp( ( progress - fade_start ) / max( 1.0 - fade_start, 0.0001 ), 0.0, 1.0 );
          vAlpha = fade * max_alpha;

          // Ease-out expansion curve
          float max_scale = aParams.x;
          float decel_power = aParams.z;
          float ease_dist = 1.0 - pow( 1.0 - progress, decel_power );
          float current_scale = max_scale * ease_dist;

          // Billboard facing camera in view space
          vec4 mv_position = modelViewMatrix * vec4( aPosTime.xyz, 1.0 );
          mv_position.xy += position.xy * current_scale;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform vec4 uGlowUv;

        varying vec2 vQuadUv;
        varying float vAlpha;

        void main() {
          // Sample ResourceID.ShockWaveGlow1 sprite from atlas directly as is
          vec2 atlas_glow_uv = mix( uGlowUv.xy, uGlowUv.zw, vQuadUv );
          vec4 glow_sample = texture2D( uTexture, atlas_glow_uv );

          // Pure white color, alpha from texture as is, faded over lifetime
          float final_alpha = glow_sample.a * vAlpha;

          gl_FragColor = vec4( vec3( 1.0 ), final_alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxShockwaveBatch.MAX_INSTANCES, layout: ShockwaveLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxShockwaveBatch.RENDER_ORDER;
  }

  private static getGeometry(): THREE.BufferGeometry
  {
    if ( VfxShockwaveBatch.cached_geometry )
      return VfxShockwaveBatch.cached_geometry;

    const geom = new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxShockwaveBatch.cached_geometry = geom;
    return geom;
  }

  public spawnParticle(
    start_pos: THREE.Vector3,
    max_scale: number,
    duration: number,
    start_time: number,
    deceleration_power: number = 3.0,
    max_alpha: number = 1.0,
    fade_start_progress: number = 0.0
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !this.texture_initialized || !mat.uniforms.uTexture.value )
    {
      try
      {
        mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture();
        const glow_uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.ShockwaveGlow1 );
        mat.uniforms.uGlowUv.value.set( glow_uv.min_x, glow_uv.min_y, glow_uv.max_x, glow_uv.max_y );
        this.texture_initialized = true;
      }
      catch ( _e ) {}
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const pos_time_attr    = this.geometry.getAttribute( "aPosTime" ).array as Float32Array;
    const params_attr      = this.geometry.getAttribute( "aParams" ).array as Float32Array;
    const fade_params_attr = this.geometry.getAttribute( "aFadeParams" ).array as Float32Array;

    pos_time_attr[ idx * 4 ]     = start_pos.x;
    pos_time_attr[ idx * 4 + 1 ] = start_pos.y;
    pos_time_attr[ idx * 4 + 2 ] = start_pos.z;
    pos_time_attr[ idx * 4 + 3 ] = start_time;

    params_attr[ idx * 3 ]     = max_scale;
    params_attr[ idx * 3 + 1 ] = duration;
    params_attr[ idx * 3 + 2 ] = deceleration_power;

    fade_params_attr[ idx * 2 ]     = fade_start_progress;
    fade_params_attr[ idx * 2 + 1 ] = max_alpha;

    this.geometry.getAttribute( "aPosTime" ).needsUpdate    = true;
    this.geometry.getAttribute( "aParams" ).needsUpdate     = true;
    this.geometry.getAttribute( "aFadeParams" ).needsUpdate = true;

    return this.getStableId( idx );
  }


  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
